import { z } from 'zod';
import { shoppingPreferencesSchema } from './shopping.js';
import { currencyCodeSchema } from './prices.js';
import { tastingPreferencesSchema } from './tasting.js';
import { isoDateTimeSchema, localeSchema, uuidSchema } from './common.js';
import { dietaryLabelSchema, measurementSystemSchema } from './enums.js';
import { BACKGROUND_DIM_DEFAULT, backgroundDimSchema, backgroundStyleSchema, colorModeSchema, themeSchema, } from './themes.js';
export const speechPreferencesSchema = z.object({
    inputEnabled: z.boolean().default(true),
    readAloudEnabled: z.boolean().default(true),
    preferServerTranscription: z.boolean().default(false),
    voiceName: z.string().max(120).nullable().default(null),
    speechRate: z.number().min(0.5).max(2).default(1),
    language: localeSchema.nullable().default(null),
});
export const userProfileSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    displayName: z.string(),
    avatarAssetId: uuidSchema.nullable(),
    avatarUrl: z.string().nullable(),
    locale: localeSchema,
    measurementSystem: measurementSystemSchema,
    enableUnitConversion: z.boolean(),
    defaultServings: z.number().int().positive(),
    dietaryPreferences: z.array(dietaryLabelSchema),
    avoidedIngredients: z.array(z.string()),
    speechPreferences: speechPreferencesSchema,
    tastingPreferences: tastingPreferencesSchema,
    shoppingPreferences: shoppingPreferencesSchema,
    /** The user's own choice (ISO 4217); null = the instance default. */
    currency: currencyCodeSchema.nullable().default(null),
    /** What applies: `currency`, else the instance default. */
    effectiveCurrency: currencyCodeSchema.default('EUR'),
    /**
     * Appearance (docs/partial/appearance-and-themes-spec.md 6.5). `theme` is always one that
     * exists and is enabled: a removed one reads as the installation default.
     */
    theme: themeSchema.default('classic'),
    colorMode: colorModeSchema.default('SYSTEM'),
    backgroundStyle: backgroundStyleSchema.default('PATTERN'),
    backgroundDim: backgroundDimSchema.default(BACKGROUND_DIM_DEFAULT),
    backgroundAssetId: uuidSchema.nullable().default(null),
    /** Signed, short-lived URL of the own background photo. */
    backgroundUrl: z.string().nullable().default(null),
    /** Unlocked by an administrator; without it `aiConsent` cannot be switched on. */
    aiEnabled: z.boolean(),
    aiConsentAt: isoDateTimeSchema.nullable(),
    /** Mail to this address bounced or was reported – nothing is sent until it is changed or lifted. */
    emailDeliveryProblem: z.boolean().default(false),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const updateProfileRequestSchema = z
    .object({
    displayName: z.string().trim().min(1).max(80),
    avatarAssetId: uuidSchema.nullable(),
    locale: localeSchema,
    measurementSystem: measurementSystemSchema,
    enableUnitConversion: z.boolean(),
    defaultServings: z.number().int().min(1).max(100),
    dietaryPreferences: z.array(dietaryLabelSchema).max(20),
    avoidedIngredients: z.array(z.string().trim().min(1).max(80)).max(100),
    speechPreferences: speechPreferencesSchema.partial(),
    tastingPreferences: tastingPreferencesSchema.partial(),
    shoppingPreferences: shoppingPreferencesSchema.partial(),
    /** null = back to the instance default. Stored prices keep their currency. */
    currency: currencyCodeSchema.nullable(),
    /** A string, not the enum: an unknown id is refused with the reason `profile.themeUnknown`. */
    theme: z.string().trim().min(1).max(40),
    colorMode: colorModeSchema,
    backgroundStyle: backgroundStyleSchema,
    backgroundDim: backgroundDimSchema,
    /** The user's own asset of purpose THEME_BACKGROUND; null removes (and deletes) the photo. */
    backgroundAssetId: uuidSchema.nullable(),
    aiConsent: z.boolean(),
})
    .partial();
export const deleteAccountRequestSchema = z.object({
    password: z.string().min(1).max(128),
    confirmation: z.literal('DELETE'),
});
export const publicUserSchema = z.object({
    id: uuidSchema,
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
});
//# sourceMappingURL=users.js.map